// API functions for segments and related data

import apiClient from './api';

export interface SegmentOrder {
  [key: string]: number;
}

// Cache for API responses
let segmentsCache: string[] | null = null;
let usageTypesCache: string[] | null = null;
let segmentOrderCache: SegmentOrder | null = null;

export const fetchSegments = async (): Promise<string[]> => {
  if (segmentsCache) {
    return segmentsCache;
  }

  try {
    const segments = await apiClient.get<string[]>('/products/segments');
    segmentsCache = Array.isArray(segments) ? segments : [];
    return segmentsCache;
  } catch (error) {
    console.error('Error fetching segments:', error);
    // Fallback to hardcoded values
    return ["Ed. Infantil", "Fund. Anos Iniciais", "Fund. Anos Finais", "Ensino Médio", "PV"];
  }
};

export const fetchUsageTypes = async (): Promise<string[]> => {
  if (usageTypesCache) {
    return usageTypesCache;
  }

  try {
    const usageTypes = await apiClient.get<string[]>('/products/usage-types');
    usageTypesCache = usageTypes || [];
    return usageTypesCache;
  } catch (error) {
    console.error('Error fetching usage types:', error);
    // Fallback to hardcoded values
    return ["Aluno", "Professor"];
  }
};

export const fetchBrands = async (): Promise<string[]> => {
  try {
    const response = await apiClient.get<{ data: any[] }>('/brands');
    return response.data.map(brand => brand.name);
  } catch (error) {
    console.error('Error fetching brands:', error);
    return [];
  }
};

export const fetchSeries = async (): Promise<string[]> => {
  try {
    const series = await apiClient.get<string[]>('/products/series');
    return series || [];
  } catch (error) {
    console.error('Error fetching series:', error);
    return [];
  }
};

// Initialize segment order cache
export const initializeSegmentOrder = async (): Promise<void> => {
  if (segmentOrderCache) return;

  try {
    const segments = await fetchSegments();

    // Create order mapping based on API response
    segmentOrderCache = {};
    if (Array.isArray(segments)) {
      segments.forEach((segment, index) => {
        segmentOrderCache![segment] = index + 1;
      });
    } else {
      console.warn('Segments data is not an array:', segments);
      throw new Error('Invalid segments data format');
    }
  } catch (error) {
    console.error('Error initializing segment order:', error);
    // Fallback to hardcoded order
    segmentOrderCache = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      "PV": 5,
    };
  }
};

// Synchronous function to get segment order (must call initializeSegmentOrder first)
export const getSegmentOrder = (segmento: string | null): number => {
  if (!segmentOrderCache) {
    console.warn('Segment order cache not initialized. Call initializeSegmentOrder first.');
    // Fallback to hardcoded order
    const fallbackOrder: SegmentOrder = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      "PV": 5,
    };
    return fallbackOrder[segmento || ""] || 999;
  }

  return segmentOrderCache[segmento || ""] || 999;
};

// Clear cache (useful for refreshing data)
export const clearCache = (): void => {
  segmentsCache = null;
  usageTypesCache = null;
  segmentOrderCache = null;
};